import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';

const FaTimes = Icons.FaTimes as any;

export interface FieldOption {
  value: string;
  text: string;
}

// Searchable Select Components
const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem; /* Make room for clear button */
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &.error {
    border-color: ${props => props.theme.error};
  }

  &:disabled {
    background: ${props => props.theme.surface};
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    padding: 0.65rem;
    padding-right: 2.25rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    padding-right: 2rem;
    font-size: 0.9rem;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
    color: ${props => props.theme.error};
  }

  &:disabled {
    display: none;
  }
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${props => props.theme.border};
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownOption = styled.div<{ isSelected?: boolean; isHighlighted?: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.isSelected ? props.theme.primary + '10' :
               props.isHighlighted ? props.theme.surface : 'white'};
  color: ${props => props.isSelected ? props.theme.primary : props.theme.textPrimary};
  font-weight: ${props => props.isSelected ? '600' : '400'};

  &:hover {
    background: ${props => props.theme.surface};
  }

  @media (max-width: 768px) {
    padding: 0.65rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }
`;

const NoOptionsMessage = styled.div`
  padding: 0.75rem;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-style: italic;
`;

// Searchable Select Component
interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select option",
  disabled = false,
  required = false,
  hasError = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value);
    setDisplayValue(selectedOption ? selectedOption.text : '');
    setSearchTerm('');
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.text.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        const selectedOption = options.find(opt => opt.value === value);
        setDisplayValue(selectedOption ? selectedOption.text : '');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, options]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        const selectedOption = options.find(opt => opt.value === value);
        setDisplayValue(selectedOption ? selectedOption.text : '');
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm(displayValue);
      setDisplayValue('');
    }
  };

  const handleOptionSelect = (option: FieldOption) => {
    onChange(option.value);
    setDisplayValue(option.text);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setDisplayValue('');
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <SearchableSelectContainer ref={containerRef}>
      <SearchableInput
        type="text"
        value={isOpen ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={hasError ? 'error' : ''}
        autoComplete="off"
      />

      {value && !disabled && (
        <ClearButton
          type="button"
          onClick={handleClear}
          title="Clear selection"
        >
          <FaTimes />
        </ClearButton>
      )}

      <DropdownList isOpen={isOpen}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <DropdownOption
              key={option.value}
              isSelected={option.value === value}
              isHighlighted={index === highlightedIndex}
              onClick={() => handleOptionSelect(option)}
            >
              {option.text}
            </DropdownOption>
          ))
        ) : (
          <NoOptionsMessage>
            {searchTerm ? 'No options found' : 'No options available'}
          </NoOptionsMessage>
        )}
      </DropdownList>
    </SearchableSelectContainer>
  );
};

export default SearchableSelect;
